import { assetUsage, detachAsset } from '../domain/audioOps';
import { detachImage, imageUsage } from '../domain/sceneOps';
import { createProject } from '../domain/factories';
import { MigrationError, migrateProjectDocument } from '../domain/migrations';
import {
  MAX_AUDIO_ASSETS,
  MAX_IMAGE_ASSETS,
  assetRequiresBinary,
  MAX_PROJECT_AUDIO_BYTES,
  NameSchema,
  ProjectSchema,
  formatIssues,
  summarize,
  type Asset,
  type Project,
  type ProjectSummary,
} from '../domain/schemas';
import { createId, nowIso } from '../lib/ids';
import { STORE_BLOBS, STORE_PROJECTS, requestToPromise, runTransaction, type StoredBlob } from './db';
import { StorageError } from './errors';

export interface CorruptedRecord {
  id: string;
  reason: string;
}

export interface ProjectListing {
  projects: ProjectSummary[];
  /** Records that exist in storage but cannot be read. They are reported, never silently deleted. */
  corrupted: CorruptedRecord[];
}

export interface RepositoryOptions {
  /**
   * Test-only fault injection: called after each write is queued inside a transaction.
   * Throwing aborts the transaction, which must leave storage unchanged.
   */
  onWrite?: (store: string, key: string) => void;
}

function validName(name: string): string {
  const parsed = NameSchema.safeParse(name);
  if (!parsed.success) throw new StorageError('validation', 'Invalid project name.', formatIssues(parsed.error));
  return parsed.data;
}

function validate(project: Project): Project {
  const parsed = ProjectSchema.safeParse(project);
  if (!parsed.success) {
    throw new StorageError('validation', 'Project data is invalid and was not saved.', formatIssues(parsed.error));
  }
  return parsed.data;
}

function readStored(raw: unknown, id: string): Project {
  try {
    return migrateProjectDocument(raw);
  } catch (error) {
    const details = error instanceof MigrationError ? error.details : [];
    throw new StorageError('validation', `Stored project "${id}" is unreadable.`, details, { cause: error });
  }
}

export class ProjectRepository {
  private readonly getDb: () => Promise<IDBDatabase>;
  private readonly options: RepositoryOptions;

  constructor(getDb: () => Promise<IDBDatabase>, options: RepositoryOptions = {}) {
    this.getDb = getDb;
    this.options = options;
  }

  private put<T extends { id: string }>(store: IDBObjectStore, value: T, mode: 'add' | 'put' = 'put'): void {
    if (mode === 'add') store.add(value);
    else store.put(value);
    this.options.onWrite?.(store.name, value.id);
  }

  async list(): Promise<ProjectListing> {
    const db = await this.getDb();
    const records = await runTransaction(db, [STORE_PROJECTS], 'readonly', (tx) =>
      requestToPromise(tx.objectStore(STORE_PROJECTS).getAll() as IDBRequest<unknown[]>),
    );
    const projects: ProjectSummary[] = [];
    const corrupted: CorruptedRecord[] = [];
    for (const raw of records) {
      const id = String((raw as { id?: unknown } | null)?.id ?? 'unknown');
      try {
        projects.push(summarize(readStored(raw, id)));
      } catch (error) {
        corrupted.push({ id, reason: error instanceof Error ? error.message : 'Unreadable' });
      }
    }
    projects.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return { projects, corrupted };
  }

  async get(id: string): Promise<Project> {
    const db = await this.getDb();
    const raw = await runTransaction(db, [STORE_PROJECTS], 'readonly', (tx) =>
      requestToPromise(tx.objectStore(STORE_PROJECTS).get(id)),
    );
    if (raw === undefined) throw new StorageError('not-found', 'That project no longer exists.');
    return readStored(raw, id);
  }

  async create(name: string, description = ''): Promise<Project> {
    const project = validate(createProject(validName(name), description));
    const db = await this.getDb();
    await runTransaction(db, [STORE_PROJECTS], 'readwrite', (tx) => {
      this.put(tx.objectStore(STORE_PROJECTS), project, 'add');
    });
    return project;
  }

  /**
   * Persists a full project document. The project must already exist: this prevents a
   * pending autosave from resurrecting a project deleted in another tab.
   */
  async save(project: Project): Promise<Project> {
    const valid = validate(project);
    const db = await this.getDb();
    await runTransaction(db, [STORE_PROJECTS], 'readwrite', async (tx) => {
      const store = tx.objectStore(STORE_PROJECTS);
      const existing = await requestToPromise(store.getKey(valid.id));
      if (existing === undefined) throw new StorageError('not-found', 'This project was deleted; changes were not saved.');
      this.put(store, valid);
    });
    return valid;
  }

  async rename(id: string, name: string): Promise<Project> {
    const project = await this.get(id);
    return this.save({ ...project, name: validName(name), updatedAt: nowIso() });
  }

  async duplicate(id: string): Promise<Project> {
    const source = await this.get(id);
    const db = await this.getDb();
    const blobs = await this.blobsFor(db, id);
    const idMap = new Map(source.assets.map((a) => [a.id, createId('ast')]));
    const now = nowIso();
    const copy = validate(
      remapAssets(
        {
          ...source,
          id: createId('prj'),
          name: `${source.name} (copy)`.slice(0, 80),
          createdAt: now,
          updatedAt: now,
        },
        idMap,
      ),
    );
    await runTransaction(db, [STORE_PROJECTS, STORE_BLOBS], 'readwrite', (tx) => {
      this.put(tx.objectStore(STORE_PROJECTS), copy, 'add');
      const blobStore = tx.objectStore(STORE_BLOBS);
      for (const blob of blobs) {
        const newId = idMap.get(blob.id);
        if (newId) this.put(blobStore, { ...blob, id: newId, projectId: copy.id }, 'add');
      }
    });
    return copy;
  }

  /** Deletes a project and all of its stored asset blobs atomically. */
  async delete(id: string): Promise<void> {
    const db = await this.getDb();
    await runTransaction(db, [STORE_PROJECTS, STORE_BLOBS], 'readwrite', async (tx) => {
      tx.objectStore(STORE_PROJECTS).delete(id);
      const keys = await requestToPromise(tx.objectStore(STORE_BLOBS).index('projectId').getAllKeys(id));
      for (const key of keys) tx.objectStore(STORE_BLOBS).delete(key);
    });
  }

  /**
   * Inserts a fully validated project together with its blobs in one transaction.
   * Used by import; the caller is responsible for assigning non-conflicting ids.
   */
  async insert(project: Project, blobs: StoredBlob[] = []): Promise<Project> {
    const valid = validate(project);
    const db = await this.getDb();
    await runTransaction(db, [STORE_PROJECTS, STORE_BLOBS], 'readwrite', (tx) => {
      this.put(tx.objectStore(STORE_PROJECTS), valid, 'add');
      for (const blob of blobs) this.put(tx.objectStore(STORE_BLOBS), { ...blob, projectId: valid.id }, 'add');
    });
    return valid;
  }

  async exists(id: string): Promise<boolean> {
    const db = await this.getDb();
    const key = await runTransaction(db, [STORE_PROJECTS], 'readonly', (tx) =>
      requestToPromise(tx.objectStore(STORE_PROJECTS).getKey(id)),
    );
    return key !== undefined;
  }

  /** Adds a local asset (metadata + binary) to a project atomically. */
  async addAsset(project: Project, asset: Asset, data: ArrayBuffer): Promise<Project> {
    if (asset.kind === 'audio' || asset.kind === 'image') {
      const limit = asset.kind === 'audio' ? MAX_AUDIO_ASSETS : MAX_IMAGE_ASSETS;
      if (project.assets.filter((a) => a.kind === asset.kind).length >= limit) {
        throw new StorageError('validation', `A project can hold at most ${limit} ${asset.kind} files. Delete unused ones first.`);
      }
      // One budget for every stored file (audio and images), so the project always fits
      // the 50 MB import limit and can round-trip through a JSON export.
      const total = projectBinaryBytes(project) + asset.sizeBytes;
      if (total > MAX_PROJECT_AUDIO_BYTES) {
        throw new StorageError(
          'validation',
          `This file would bring the project to ${(total / 1048576).toFixed(1)} MB of audio and images; the limit is ${MAX_PROJECT_AUDIO_BYTES / 1048576} MB.`,
        );
      }
    }
    if (data.byteLength !== asset.sizeBytes) throw new StorageError('validation', 'The file size does not match its data.');
    const updated = validate({ ...project, assets: [...project.assets, asset], updatedAt: nowIso() });
    const db = await this.getDb();
    await runTransaction(db, [STORE_PROJECTS, STORE_BLOBS], 'readwrite', async (tx) => {
      const store = tx.objectStore(STORE_PROJECTS);
      const existing = await requestToPromise(store.getKey(updated.id));
      if (existing === undefined) throw new StorageError('not-found', 'That project no longer exists.');
      this.put(tx.objectStore(STORE_BLOBS), { id: asset.id, projectId: project.id, mimeType: asset.mimeType, data });
      this.put(store, updated);
    });
    return updated;
  }

  /**
   * Deletes an asset and its binary atomically. Refuses (code 'conflict') while a
   * sample slot or clip uses it, unless `detach` is set, in which case those
   * references are removed in the same transaction. Assets are owned by one project
   * (duplicates copy them), so no other project can be affected.
   */
  async removeAsset(project: Project, assetId: string, options: { detach?: boolean } = {}): Promise<Project> {
    const usage = assetUsage(project, assetId);
    const images = imageUsage(project, assetId);
    const used = usage.instruments.length + usage.clipIds.length + images.objects.length + images.backgrounds.length;
    if (used > 0 && !options.detach) {
      throw new StorageError('conflict', 'This file is still in use.', [
        ...usage.instruments.map((i) => `Sample on the ${i} track`),
        ...(usage.clipIds.length ? [`${usage.clipIds.length} clip(s) on the timeline`] : []),
        ...(images.objects.length ? [`${images.objects.length} image object(s) in scenes`] : []),
        ...(images.backgrounds.length ? [`Background of ${images.backgrounds.length} scene(s)`] : []),
      ]);
    }
    const updated = validate({ ...withoutAsset(project, assetId), updatedAt: nowIso() });
    const db = await this.getDb();
    await runTransaction(db, [STORE_PROJECTS, STORE_BLOBS], 'readwrite', async (tx) => {
      const store = tx.objectStore(STORE_PROJECTS);
      const existing = await requestToPromise(store.getKey(updated.id));
      if (existing === undefined) throw new StorageError('not-found', 'That project no longer exists.');
      const blobs = tx.objectStore(STORE_BLOBS);
      const blob = (await requestToPromise(blobs.get(assetId))) as StoredBlob | undefined;
      // Never delete a blob that belongs to a different project.
      if (blob && blob.projectId === project.id) blobs.delete(assetId);
      this.put(store, updated);
    });
    return updated;
  }

  async getBlob(assetId: string): Promise<StoredBlob | undefined> {
    const db = await this.getDb();
    return runTransaction(db, [STORE_BLOBS], 'readonly', (tx) =>
      requestToPromise(tx.objectStore(STORE_BLOBS).get(assetId) as IDBRequest<StoredBlob | undefined>),
    );
  }

  async blobsForProject(projectId: string): Promise<StoredBlob[]> {
    return this.blobsFor(await this.getDb(), projectId);
  }

  private blobsFor(db: IDBDatabase, projectId: string): Promise<StoredBlob[]> {
    return runTransaction(db, [STORE_BLOBS], 'readonly', (tx) =>
      requestToPromise(tx.objectStore(STORE_BLOBS).index('projectId').getAll(projectId) as IDBRequest<StoredBlob[]>),
    );
  }
}

/** Rewrites asset ids (and references to them) using the provided mapping. */
/** Bytes of every stored binary in a project (audio and images). */
/**
 * A project without one asset and everything that used it: sample slots and clips (audio),
 * image objects and backgrounds, and the animation tracks, reactions and cues that pointed
 * at those objects. Pure (the input is not modified). Shared by `removeAsset` and the
 * emergency backup, which drops files that are no longer stored.
 */
export function withoutAsset(project: Project, assetId: string): Project {
  const base = detachImage(detachAsset(project, assetId), assetId);
  return {
    ...base,
    assets: base.assets.filter((a) => a.id !== assetId),
    outputs: base.outputs.map((o) => (o.assetId === assetId ? { ...o, assetId: undefined } : o)),
  };
}

/**
 * Every project field `removeAsset` can change: the asset list and outputs, sample slots
 * and clips (audio), scene objects and backgrounds (images), and the animations and
 * audio-visual sessions pruned with deleted objects.
 */
export const ASSET_REMOVAL_FIELDS = ['assets', 'outputs', 'tracks', 'audioClips', 'scenes', 'animations', 'avSessions'] as const;

/**
 * Applies a completed `removeAsset` to the open (in-memory) project. Copying only some of
 * these fields leaves references to deleted objects behind, and every later save fails.
 */
export function withAssetRemoved(live: Project, saved: Project): Project {
  const next: Project = { ...live };
  for (const field of ASSET_REMOVAL_FIELDS) (next as Record<string, unknown>)[field] = saved[field];
  return next;
}

export const projectBinaryBytes = (project: Project) =>
  project.assets.filter((a) => assetRequiresBinary(a)).reduce((sum, a) => sum + a.sizeBytes, 0);

export function remapAssets(project: Project, idMap: Map<string, string>): Project {
  return {
    ...project,
    assets: project.assets.map((a) => ({ ...a, id: idMap.get(a.id) ?? a.id })),
    outputs: project.outputs.map((o) => (o.assetId ? { ...o, assetId: idMap.get(o.assetId) ?? o.assetId } : o)),
    tracks: project.tracks.map((t) =>
      t.sample ? { ...t, sample: { ...t.sample, assetId: idMap.get(t.sample.assetId) ?? t.sample.assetId } } : t,
    ),
    audioClips: project.audioClips.map((c) => ({ ...c, assetId: idMap.get(c.assetId) ?? c.assetId })),
    scenes: project.scenes.map((s) => ({
      ...s,
      backgroundImage: s.backgroundImage ? { ...s.backgroundImage, assetId: idMap.get(s.backgroundImage.assetId) ?? s.backgroundImage.assetId } : null,
      objects: s.objects.map((o) => (o.type === 'image' ? { ...o, assetId: idMap.get(o.assetId) ?? o.assetId } : o)),
    })),
  };
}
